import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface Mentor {
  id: string;
  user_id: string;
  headline: string;
  bio: string;
  expertise: string[];
  countries: string[];
  languages: string[];
  sessionFormats: string[];
  is_free: number;
  hourly_rate: number | null;
  response_time: string;
  request_count: number;
  created_at: string;
  user_name: string;
  avatar_url: string | null;
  country_code: string | null;
  email?: string;
}

export interface MentorshipRequest {
  id: string;
  mentor_user_id: string;
  mentee_user_id: string;
  message: string;
  expertise_sought: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'completed';
  created_at: string;
  responded_at: string | null;
  // Populated depending on role
  mentor_name?: string;
  mentor_avatar?: string;
  mentor_headline?: string;
  mentor_profile_id?: string;
  mentee_name?: string;
  mentee_avatar?: string;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useMentors(filters: { expertise?: string; country?: string; free?: boolean } = {}) {
  const params = new URLSearchParams();
  if (filters.expertise) params.set('expertise', filters.expertise);
  if (filters.country) params.set('country', filters.country);
  if (filters.free) params.set('free', 'true');

  return useQuery({
    queryKey: ['mentors', filters],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/mentorship/mentors?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load mentors');
      return res.json() as Promise<{ mentors: Mentor[] }>;
    },
  });
}

export function useMentor(id: string | undefined) {
  return useQuery({
    queryKey: ['mentor', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/mentorship/mentors/${id}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Not found');
      return res.json() as Promise<{ mentor: Mentor }>;
    },
  });
}

export function useMyMentorProfile() {
  return useQuery({
    queryKey: ['my-mentor-profile'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/mentorship/mentors/me`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load');
      return res.json() as Promise<{ mentor: Mentor | null }>;
    },
  });
}

export function useRegisterMentor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Mentor> & { isFree: boolean; hourlyRate?: number; responseTime?: string }) => {
      const res = await fetch(`${API_URL}/api/mentorship/mentors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json() as any;
        throw new Error(err.error || 'Failed');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mentors'] });
      qc.invalidateQueries({ queryKey: ['my-mentor-profile'] });
    },
  });
}

export function useUpdateMentorProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Mentor> & { isFree?: boolean; hourlyRate?: number; isAvailable?: boolean }) => {
      const res = await fetch(`${API_URL}/api/mentorship/mentors/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-mentor-profile'] }),
  });
}

export function useSendMentorshipRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { mentorId: string; message: string; expertiseSought?: string }) => {
      const res = await fetch(`${API_URL}/api/mentorship/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json() as any;
        throw new Error(err.error || 'Failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mentorship-requests'] }),
  });
}

export function useMentorshipRequests(role: 'mentor' | 'mentee' = 'mentee') {
  return useQuery({
    queryKey: ['mentorship-requests', role],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/mentorship/requests?role=${role}`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      return res.json() as Promise<{ requests: MentorshipRequest[] }>;
    },
  });
}

export function useRespondToRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'accepted' | 'declined' | 'completed' }) => {
      const res = await fetch(`${API_URL}/api/mentorship/requests/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mentorship-requests'] }),
  });
}

export const EXPERTISE_OPTIONS = [
  { value: 'visa', label: 'Visa & Immigration' },
  { value: 'housing', label: 'Finding Housing' },
  { value: 'career', label: 'Career & Jobs' },
  { value: 'language', label: 'Language Barrier' },
  { value: 'culture', label: 'Cultural Adjustment' },
  { value: 'education', label: 'Education' },
  { value: 'finance', label: 'Banking & Finance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'business', label: 'Starting a Business' },
  { value: 'family', label: 'Family & Kids' },
];

export const COUNTRY_OPTIONS = [
  { value: 'US', label: '🇺🇸 United States' },
  { value: 'CA', label: '🇨🇦 Canada' },
  { value: 'AU', label: '🇦🇺 Australia' },
  { value: 'GB', label: '🇬🇧 United Kingdom' },
  { value: 'DE', label: '🇩🇪 Germany' },
  { value: 'FR', label: '🇫🇷 France' },
  { value: 'JP', label: '🇯🇵 Japan' },
  { value: 'SG', label: '🇸🇬 Singapore' },
  { value: 'NZ', label: '🇳🇿 New Zealand' },
  { value: 'MX', label: '🇲🇽 Mexico' },
  { value: 'NL', label: '🇳🇱 Netherlands' },
  { value: 'ID', label: '🇮🇩 Indonesia' },
  { value: 'TH', label: '🇹🇭 Thailand' },
  { value: 'UAE', label: '🇦🇪 UAE' },
];
